import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { ClipboardCheck, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { ImportFromErpLink } from './ImportFromErpLink';
import { Card } from '@siliconx/shared/ui/Card';
import { DataTable } from '@siliconx/shared/ui/DataTable';

/**
 * SIL-271 (W4-T11, spec decision 9) — "N of M assets have financial data"
 * coverage stat + filtered gap list. Consumes `GET
 * /api/client/financials/coverage` (SIL-269, W3-T9), seller-only same as
 * every other financials report endpoint. Mounted on the Financials index
 * (book-vs-fmv.tsx, the screen `/financials/` redirects to) — the import
 * summary's copy of this same stat is SIL-270's wizard screen, out of this
 * branch's scope.
 */
interface CoverageGap {
  assetId: string;
  assetClassName: string;
  serialNumber: string | null;
  assetTag: string | null;
}

const GAP_COLUMNS: ColumnDef<CoverageGap, unknown>[] = [
  {
    id: 'assetId',
    header: 'Asset',
    cell: (ctx) => (
      // The pre-DataTable markup made each row a <Link className="table-row">,
      // so every gap was tab-reachable. onRowClick is a bare <tr onClick> with
      // no tabIndex or key handler, so keep a real link in the identity cell.
      <Link
        to="/assets/$assetId"
        params={{ assetId: ctx.row.original.assetId }}
        className="font-mono text-xs text-zinc-500 hover:text-brand-600 group-hover:text-brand-600"
        onClick={(e) => e.stopPropagation()}
      >
        {ctx.row.original.assetId.slice(0, 8)}
      </Link>
    ),
  },
  {
    id: 'assetClassName',
    header: 'Class',
    cell: (ctx) => <span className="text-zinc-700">{ctx.row.original.assetClassName}</span>,
  },
  {
    id: 'serialNumber',
    header: 'Serial',
    cell: (ctx) => (
      <span className="text-zinc-600">{ctx.row.original.serialNumber ?? '—'}</span>
    ),
  },
  {
    id: 'assetTag',
    header: 'Asset tag',
    cell: (ctx) => (
      <span className="inline-flex items-center gap-1 text-zinc-600">
        {ctx.row.original.assetTag ?? '—'}
        <ExternalLink className="size-3 shrink-0 text-zinc-300 group-hover:text-brand-600" />
      </span>
    ),
  },
];

export function CoverageStatCard() {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const coverageQ = useQuery({
    queryKey: qk.financials.coverage(),
    queryFn: async () => {
      const res = await api.api.client.financials.coverage.$get({ query: { limit: '25' } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return res.json();
    },
  });

  if (coverageQ.isLoading) {
    return (
      <Card padding="md">
        <div className="text-sm text-zinc-500">Loading financial-data coverage…</div>
      </Card>
    );
  }
  if (coverageQ.error || !coverageQ.data) {
    return null; // non-fatal — the rest of the Financials page still renders.
  }

  const { totalAssets, assetsWithFinancialData, gaps } = coverageQ.data;
  const complete = totalAssets > 0 && assetsWithFinancialData >= totalAssets;
  const pct = totalAssets > 0 ? Math.round((assetsWithFinancialData / totalAssets) * 100) : 0;

  return (
    <Card padding="md">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex size-9 shrink-0 items-center justify-center rounded-full ${
              complete ? 'bg-emerald-50' : 'bg-amber-50'
            }`}
          >
            <ClipboardCheck className={`size-5 ${complete ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-zinc-900">
              {assetsWithFinancialData.toLocaleString()} of {totalAssets.toLocaleString()} assets have
              financial data
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">
              {complete
                ? 'Every asset has purchase price / book-value facts recorded.'
                : `${pct}% coverage — the rest fall back to estimated book value.`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!complete && <ImportFromErpLink />}
          {gaps.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700"
            >
              {expanded ? 'Hide gaps' : `Show ${gaps.length} gap${gaps.length === 1 ? '' : 's'}`}
              {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
            </button>
          )}
        </div>
      </div>

      {expanded && gaps.length > 0 && (
        <div className="mt-4 border-t border-zinc-100 pt-3">
          <DataTable
            data={gaps}
            columns={GAP_COLUMNS}
            onRowClick={(g) => navigate({ to: '/assets/$assetId', params: { assetId: g.assetId } })}
          />
        </div>
      )}
    </Card>
  );
}
